import React, { useState } from "react";
import { toast } from "react-toastify";
import * as CampaignContentAPI from "../../services/api/CampaignContent";
import {
  MarketingCampaignDoc,
  MarketingCampaignStatus,
  MarketingCampaignCTA,
  MarketingCampaignSecondaryCTA,
  MarketingCampaignAsset,
  PrimaryCTAType,
  DMCampaignDropdownOptions,
} from "@pollyslack/hub/src/types";
import { CampaignPreview } from "./CampaignPreview";

const PRIMARY_CTA_OPTIONS = Object.entries(PrimaryCTAType).map(
  ([key, value]) => ({ key, value }),
);

const DROPDOWN_OPTIONS = Object.entries(DMCampaignDropdownOptions).map(
  ([key, value]) => ({ key, value }),
);

const LINK_CTA_TYPES: string[] = [PrimaryCTAType.LinkButton];

interface CampaignFormProps {
  /** The campaign being edited. Leave unset to create one. */
  campaign?: MarketingCampaignDoc;
  /** Prefills a new campaign, e.g. the one being duplicated */
  initialValues?: Partial<MarketingCampaignDoc>;
  onSave: () => void;
  onCancel: () => void;
}

export const CampaignForm: React.FC<CampaignFormProps> = ({
  campaign: editedCampaign,
  initialValues,
  onSave,
  onCancel,
}) => {
  const isEdit = !!editedCampaign;
  const campaign = editedCampaign || initialValues;
  // Enrollments, sent-DM history and instrumentation all key on the
  // identifier, so it's fixed once a campaign has been active. A draft has
  // never been sendable, so it can still be renamed (PS-5765).
  const identifierLocked =
    isEdit && editedCampaign.status !== MarketingCampaignStatus.DRAFT;

  const [identifier, setIdentifier] = useState(campaign?.identifier || "");
  const [headerText, setHeaderText] = useState(campaign?.headerText || "");
  const [bodyMrkdwn, setBodyMrkdwn] = useState(campaign?.bodyMrkdwn || "");

  // Asset
  const [assetUrl, setAssetUrl] = useState(campaign?.asset?.url || "");
  const [assetAltText, setAssetAltText] = useState(
    campaign?.asset?.altText || "",
  );

  // Primary CTA
  const [primaryCTAType, setPrimaryCTAType] = useState<PrimaryCTAType>(
    campaign?.primaryCTA?.type || PrimaryCTAType.CreatePolly,
  );
  const [primaryCTALabel, setPrimaryCTALabel] = useState(
    campaign?.primaryCTA?.label || "",
  );
  const [primaryCTAUrl, setPrimaryCTAUrl] = useState(
    campaign?.primaryCTA?.url || "",
  );

  // Secondary CTA
  const [secondaryCTAMode, setSecondaryCTAMode] = useState<
    "none" | "dropdown" | "button"
  >(campaign?.secondaryCTA?.mode || "none");
  const [dropdownOptions, setDropdownOptions] = useState<
    DMCampaignDropdownOptions[]
  >(campaign?.secondaryCTA?.dropdownOptions || []);
  const [secondaryButtonType, setSecondaryButtonType] =
    useState<PrimaryCTAType>(
      campaign?.secondaryCTA?.button?.type || PrimaryCTAType.CreatePolly,
    );
  const [secondaryButtonLabel, setSecondaryButtonLabel] = useState(
    campaign?.secondaryCTA?.button?.label || "",
  );
  const [secondaryButtonUrl, setSecondaryButtonUrl] = useState(
    campaign?.secondaryCTA?.button?.url || "",
  );

  // Demo video for the Watch A Demo button / "See demo" option (PS-5765)
  const [demoUrl, setDemoUrl] = useState(campaign?.demoUrl || "");
  const usesDemo =
    primaryCTAType === PrimaryCTAType.WatchADemo ||
    (secondaryCTAMode === "button" &&
      secondaryButtonType === PrimaryCTAType.WatchADemo) ||
    (secondaryCTAMode === "dropdown" &&
      dropdownOptions.includes(DMCampaignDropdownOptions.SEE_DEMO));

  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  function buildPrimaryCTA(): MarketingCampaignCTA {
    const cta: MarketingCampaignCTA = {
      type: primaryCTAType,
      label: primaryCTALabel,
    };
    if (LINK_CTA_TYPES.includes(primaryCTAType) && primaryCTAUrl) {
      cta.url = primaryCTAUrl;
    }
    return cta;
  }

  function buildSecondaryCTA(): MarketingCampaignSecondaryCTA | undefined {
    if (secondaryCTAMode === "none") return undefined;
    if (secondaryCTAMode === "dropdown") {
      return {
        mode: "dropdown",
        dropdownOptions,
      };
    }
    const button: MarketingCampaignCTA = {
      type: secondaryButtonType,
      label: secondaryButtonLabel,
    };
    if (LINK_CTA_TYPES.includes(secondaryButtonType) && secondaryButtonUrl) {
      button.url = secondaryButtonUrl;
    }
    return { mode: "button", button };
  }

  function buildAsset(): MarketingCampaignAsset | undefined {
    if (!assetUrl) return undefined;
    return { url: assetUrl, altText: assetAltText || "Campaign image" };
  }

  async function handleSave() {
    if (!identifier.trim()) {
      toast.error("Identifier is required");
      return;
    }
    if (!headerText.trim()) {
      toast.error("Header text is required");
      return;
    }
    if (!bodyMrkdwn.trim()) {
      toast.error("Body text is required");
      return;
    }
    if (!primaryCTALabel.trim()) {
      toast.error("Primary CTA label is required");
      return;
    }
    if (
      usesDemo &&
      demoUrl.trim() &&
      !/^https?:\/\/\S+$/.test(demoUrl.trim())
    ) {
      toast.error("Demo URL must start with http:// or https://");
      return;
    }
    // Only keep a demo URL a CTA can actually send
    const savedDemoUrl = usesDemo ? demoUrl.trim() : "";

    setSaving(true);
    try {
      if (isEdit) {
        await CampaignContentAPI.updateCampaign(editedCampaign._id, {
          ...(!identifierLocked && { identifier: identifier.trim() }),
          headerText,
          bodyMrkdwn,
          primaryCTA: buildPrimaryCTA(),
          asset: buildAsset() || null,
          secondaryCTA: buildSecondaryCTA() || null,
          demoUrl: savedDemoUrl || null,
        });
        toast.success("Campaign updated");
      } else {
        await CampaignContentAPI.createCampaign({
          identifier: identifier.trim(),
          headerText,
          bodyMrkdwn,
          primaryCTA: buildPrimaryCTA(),
          asset: buildAsset(),
          secondaryCTA: buildSecondaryCTA(),
          demoUrl: savedDemoUrl || undefined,
        });
        toast.success("Campaign created");
      }
      onSave();
    } catch (error: any) {
      toast.error(error.message || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  }

  function toggleDropdownOption(opt: DMCampaignDropdownOptions) {
    setDropdownOptions((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt],
    );
  }

  return (
    <div className="row">
      <div className="col-md-7">
        <h5 className="mb-3">
          {isEdit
            ? "Edit Campaign"
            : initialValues
            ? "Duplicate Campaign"
            : "Create Campaign"}
        </h5>

        {/* Identifier */}
        <div className="form-group">
          <label className="font-weight-bold">Identifier</label>
          <input
            className="form-control"
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            disabled={identifierLocked}
            placeholder="e.g., 2026-q2-summer-promo"
          />
          <small className="form-text text-muted">
            Unique slug used for instrumentation and enrollment. Letters,
            digits, - and _ only. Can be changed until the campaign is
            activated.
          </small>
        </div>

        {/* Header Text */}
        <div className="form-group">
          <label className="font-weight-bold">Header Text</label>
          <input
            className="form-control"
            type="text"
            value={headerText}
            onChange={(e) => setHeaderText(e.target.value)}
            placeholder=":tada: Your campaign headline here"
          />
          <small className="form-text text-muted">
            Supports :emoji: syntax. Also used as Slack notification text.
          </small>
        </div>

        {/* Body (mrkdwn) */}
        <div className="form-group">
          <label className="font-weight-bold">Body (Slack mrkdwn)</label>
          <textarea
            className="form-control"
            rows={6}
            value={bodyMrkdwn}
            onChange={(e) => setBodyMrkdwn(e.target.value)}
            placeholder="Your message body here. Supports *bold*, _italic_, ~strikethrough~, <url|link text>"
            style={{ fontFamily: "monospace", fontSize: 13 }}
          />
          <small className="form-text text-muted">
            {bodyMrkdwn.length}/2900 characters (Slack limit)
          </small>
        </div>

        {/* Asset */}
        <div className="form-group">
          <label className="font-weight-bold">Image/GIF (optional)</label>
          <input
            className="form-control mb-1"
            type="text"
            value={assetUrl}
            onChange={(e) => setAssetUrl(e.target.value)}
            placeholder="https://example.com/image.png"
          />
          {assetUrl && (
            <input
              className="form-control"
              type="text"
              value={assetAltText}
              onChange={(e) => setAssetAltText(e.target.value)}
              placeholder="Alt text for the image"
            />
          )}
        </div>

        {/* Primary CTA */}
        <div className="card p-3 mb-3">
          <h6 className="font-weight-bold">Primary CTA</h6>
          <div className="form-group">
            <label>Type</label>
            <select
              className="form-control"
              value={primaryCTAType}
              onChange={(e) =>
                setPrimaryCTAType(e.target.value as PrimaryCTAType)
              }
            >
              {PRIMARY_CTA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.key}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Button Label</label>
            <input
              className="form-control"
              type="text"
              value={primaryCTALabel}
              onChange={(e) => setPrimaryCTALabel(e.target.value)}
              placeholder=":zap: Start Trial"
            />
          </div>
          {LINK_CTA_TYPES.includes(primaryCTAType) && (
            <div className="form-group">
              <label>URL</label>
              <input
                className="form-control"
                type="text"
                value={primaryCTAUrl}
                onChange={(e) => setPrimaryCTAUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          )}
        </div>

        {/* Secondary CTA */}
        <div className="card p-3 mb-3">
          <h6 className="font-weight-bold">Secondary CTA</h6>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="secondaryCTA"
              id="cta-none"
              checked={secondaryCTAMode === "none"}
              onChange={() => setSecondaryCTAMode("none")}
            />
            <label className="form-check-label" htmlFor="cta-none">
              None
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="secondaryCTA"
              id="cta-dropdown"
              checked={secondaryCTAMode === "dropdown"}
              onChange={() => setSecondaryCTAMode("dropdown")}
            />
            <label className="form-check-label" htmlFor="cta-dropdown">
              Dropdown
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="secondaryCTA"
              id="cta-button"
              checked={secondaryCTAMode === "button"}
              onChange={() => setSecondaryCTAMode("button")}
            />
            <label className="form-check-label" htmlFor="cta-button">
              Button
            </label>
          </div>

          {secondaryCTAMode === "dropdown" && (
            <div className="mt-2">
              <label className="d-block mb-1">Options</label>
              {DROPDOWN_OPTIONS.map((opt) => (
                <div className="form-check" key={opt.value}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`dd-${opt.value}`}
                    checked={dropdownOptions.includes(
                      opt.value as DMCampaignDropdownOptions,
                    )}
                    onChange={() =>
                      toggleDropdownOption(
                        opt.value as DMCampaignDropdownOptions,
                      )
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`dd-${opt.value}`}
                  >
                    {opt.value}
                  </label>
                </div>
              ))}
            </div>
          )}

          {secondaryCTAMode === "button" && (
            <div className="mt-2">
              <div className="form-group">
                <label>Type</label>
                <select
                  className="form-control"
                  value={secondaryButtonType}
                  onChange={(e) =>
                    setSecondaryButtonType(e.target.value as PrimaryCTAType)
                  }
                >
                  {PRIMARY_CTA_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.key}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Button Label</label>
                <input
                  className="form-control"
                  type="text"
                  value={secondaryButtonLabel}
                  onChange={(e) => setSecondaryButtonLabel(e.target.value)}
                  placeholder="Button text"
                />
              </div>
              {LINK_CTA_TYPES.includes(secondaryButtonType) && (
                <div className="form-group">
                  <label>URL</label>
                  <input
                    className="form-control"
                    type="text"
                    value={secondaryButtonUrl}
                    onChange={(e) => setSecondaryButtonUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Demo video (Watch A Demo button / "See demo" option) */}
        {usesDemo && (
          <div className="card p-3 mb-3">
            <h6 className="font-weight-bold">Demo Video</h6>
            <div className="form-group mb-0">
              <label>Demo URL</label>
              <input
                className="form-control"
                type="text"
                value={demoUrl}
                onChange={(e) => setDemoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <small className="form-text text-muted">
                The Watch A Demo button opens this video. The &quot;See
                demo&quot; dropdown option sends it in a follow-up DM, since
                dropdowns can&apos;t open links. Leave blank for the default
                &quot;Make your next polly pop&quot; video.
              </small>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mb-4">
          <button
            className="btn btn-primary mr-2"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && (
              <span
                className="spinner-border spinner-border-sm mr-1"
                role="status"
              />
            )}
            {isEdit ? "Update" : "Create Draft"}
          </button>
          <button
            className="btn btn-outline-secondary mr-2"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </button>
          <button className="btn btn-link" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>

      {/* Preview panel */}
      <div className="col-md-5">
        {showPreview && (
          <div>
            <h6 className="font-weight-bold mb-2">Slack Preview</h6>
            <CampaignPreview
              headerText={headerText}
              bodyMrkdwn={bodyMrkdwn}
              asset={buildAsset()}
              primaryCTA={buildPrimaryCTA()}
              secondaryCTA={buildSecondaryCTA()}
            />
          </div>
        )}
      </div>
    </div>
  );
};
